import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LifeBuoy, Mail, ArrowUpRight, Send, Loader2, X, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const MEMBERSHIP_EMAIL = 'membership@dominicanchamberfl.org';
const API = process.env.REACT_APP_BACKEND_URL;

const TOPICS = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'benefits', label: 'Membership Benefits' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'technical', label: 'Technical Support' },
  { value: 'other', label: 'Other' },
];

/* ─── Modal ──────────────────────────────────────────────────────────────── */
const SupportModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    topic: 'general',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.display_name || user.name || '',
        email: f.email || user.email || '',
      }));
    }
  }, [open, user]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error('Please write at least 10 characters');
      return;
    }
    if (!form.email || !form.email.includes('@')) {
      toast.error('A valid email is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/membership-support/contact`, form, { withCredentials: true });
      toast.success('Message sent — Membership Relations will reply soon');
      setForm({ name: '', email: '', phone: '', topic: 'general', message: '' });
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send message. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-6 bg-black/55 backdrop-blur-sm"
      onClick={onClose}
      data-testid="membership-support-modal"
    >
      <div
        className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#002249] text-white flex items-center justify-center flex-shrink-0">
            <LifeBuoy className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#E63946]">DCCF Support</p>
            <h2 className="text-[17px] font-bold text-[#002249] tracking-tight">Contact Membership Relations</h2>
            <p className="text-[12.5px] text-slate-500 mt-0.5">We typically respond within one business day.</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition"
            aria-label="Close"
            data-testid="membership-support-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-semibold text-slate-700 mb-1.5 block">Full Name</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={setField('name')}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#002249] focus:ring-2 focus:ring-[#002249]/15 outline-none text-[14px]"
                data-testid="support-field-name"
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-slate-700 mb-1.5 block">Email</label>
              <input
                type="email"
                required
                inputMode="email"
                autoComplete="email"
                value={form.email}
                onChange={setField('email')}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#002249] focus:ring-2 focus:ring-[#002249]/15 outline-none text-[14px]"
                data-testid="support-field-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-semibold text-slate-700 mb-1.5 block">
                Phone <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={setField('phone')}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#002249] focus:ring-2 focus:ring-[#002249]/15 outline-none text-[14px]"
                data-testid="support-field-phone"
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-slate-700 mb-1.5 block">Topic</label>
              <select
                value={form.topic}
                onChange={setField('topic')}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#002249] focus:ring-2 focus:ring-[#002249]/15 outline-none text-[14px] bg-white"
                data-testid="support-field-topic"
              >
                {TOPICS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[12px] font-semibold text-slate-700 mb-1.5 block">
              Message <span className="text-slate-400 font-normal">(min. 10 characters)</span>
            </label>
            <textarea
              rows={5}
              required
              value={form.message}
              onChange={setField('message')}
              maxLength={4000}
              placeholder="Tell us how we can help…"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#002249] focus:ring-2 focus:ring-[#002249]/15 outline-none text-[14px] resize-none"
              data-testid="support-field-message"
            />
            <p className="text-[11px] text-slate-400 mt-1 text-right">{form.message.length}/4000</p>
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <p className="text-[11.5px] text-slate-400">
              Direct: <a href={`mailto:${MEMBERSHIP_EMAIL}`} className="text-[#002249] hover:underline font-semibold">{MEMBERSHIP_EMAIL}</a>
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#002249] hover:bg-[#001530] disabled:opacity-60 text-white text-[13.5px] font-semibold transition-colors"
              data-testid="support-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? 'Sending…' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Card ───────────────────────────────────────────────────────────────── */
export const MembershipRelationsCard = ({
  variant = 'default',
  title = 'Membership Relations',
  body = 'Your central point of contact for all member support, benefits, and guidance.',
  showSecondary = true,
  className = '',
}) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isMember = !!user;

  const openModal = (e) => {
    e?.preventDefault?.();
    setOpen(true);
  };

  if (variant === 'compact') {
    return (
      <>
        <div
          className={`flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl ${className}`}
          data-testid="membership-relations-compact"
        >
          <div className="w-9 h-9 rounded-full bg-[#002249] text-white flex-shrink-0 flex items-center justify-center">
            <LifeBuoy className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] font-bold text-[#002249] leading-tight">{title}</p>
            {isMember ? (
              <button
                onClick={openModal}
                className="text-[11.5px] text-[#E63946] hover:text-[#c02a35] font-semibold inline-flex items-center gap-1 mt-0.5"
                data-testid="membership-relations-compact-link"
              >
                Contact Membership <ArrowUpRight className="w-3 h-3" />
              </button>
            ) : (
              <Link
                to="/member-login"
                className="text-[11.5px] text-slate-500 hover:text-[#002249] font-semibold inline-flex items-center gap-1 mt-0.5"
                data-testid="membership-relations-compact-signin"
              >
                <Lock className="w-3 h-3" /> Sign in to contact
              </Link>
            )}
          </div>
        </div>
        <SupportModal open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  if (variant === 'inverse') {
    return (
      <>
        <div
          className={`bg-[#002249] text-white rounded-2xl p-6 sm:p-8 ${className}`}
          data-testid="membership-relations-inverse"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <LifeBuoy className="w-6 h-6 text-[#E63946]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-[18px] sm:text-[20px] font-bold tracking-tight">{title}</h3>
              <p className="text-[13.5px] text-white/75 mt-1.5 leading-relaxed">{body}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {isMember ? (
                  <>
                    <button
                      onClick={openModal}
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white text-[13px] font-semibold transition-colors"
                      data-testid="membership-relations-cta-primary"
                    >
                      <Mail className="w-4 h-4" />
                      Contact Membership
                    </button>
                    {showSecondary && (
                      <button
                        onClick={openModal}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-white/20 hover:bg-white/5 text-white text-[13px] font-semibold transition-colors"
                        data-testid="membership-relations-cta-secondary"
                      >
                        Ask About Benefits
                      </button>
                    )}
                  </>
                ) : (
                  <Link
                    to="/member-login"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold transition-colors"
                    data-testid="membership-relations-signin"
                  >
                    <Lock className="w-4 h-4" />
                    Sign in to contact us
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
        <SupportModal open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  // default
  return (
    <>
      <div
        className={`bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${className}`}
        data-testid="membership-relations-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#002249] text-white flex items-center justify-center flex-shrink-0">
            <LifeBuoy className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#E63946]">DCCF Support</p>
            <h3 className="text-[18px] sm:text-[20px] font-bold text-[#002249] mt-1 tracking-tight">{title}</h3>
            <p className="text-[13.5px] text-slate-600 mt-1.5 leading-relaxed">{body}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {isMember ? (
                <>
                  <button
                    onClick={openModal}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#002249] hover:bg-[#001530] text-white text-[13px] font-semibold transition-colors"
                    data-testid="membership-relations-cta-primary"
                  >
                    <Mail className="w-4 h-4" />
                    Contact Membership
                  </button>
                  {showSecondary && (
                    <button
                      onClick={openModal}
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-[#002249] text-[13px] font-semibold transition-colors"
                      data-testid="membership-relations-cta-secondary"
                    >
                      Ask About Benefits
                    </button>
                  )}
                </>
              ) : (
                <Link
                  to="/member-login"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#002249] hover:bg-[#001530] text-white text-[13px] font-semibold transition-colors"
                  data-testid="membership-relations-signin"
                >
                  <Lock className="w-4 h-4" />
                  Sign in to contact us
                </Link>
              )}
            </div>
            <p className="mt-3 text-[11.5px] text-slate-400">
              Direct: <a href={`mailto:${MEMBERSHIP_EMAIL}`} className="text-[#002249] hover:underline font-semibold">{MEMBERSHIP_EMAIL}</a>
            </p>
          </div>
        </div>
      </div>
      <SupportModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default MembershipRelationsCard;
